import { ReactElement, useEffect, useState } from 'react';
import { useAppConstantsContext } from '@/context/AppConstants';
import { useDisconnectFromSmartcar } from '@/hooks/useDisconnectFromSmartcar';
import { reportGetters } from '@/reports/reportGetters/reportGetters';
import { useProcessClaim } from '@/reports/shared/Claiming/useProcessClaim';
import { SerializedVehicleReport } from '@/reports/types/serialized';
import { useReport } from '@/reports/useReport/useReport';
import { useReportRedirect } from '@/reports/useReportRedirect/useReportRedirect';
import { SerializeVehicleReport } from '@/reports/vehicle/vehicleReportSerializer';
import {
  Box,
  Button,
  Grid,
  IconButton,
  Icons,
  Stack,
  styled,
  Text,
  Tooltip,
} from '@/theme/index';
import {
  commaFormat,
  commaFormatCurrency,
  DateUtil,
  constructClaimedVehicleQueryParam,
} from '../../utils';
import { LoadingGarage } from '../LoadingGarage/LoadingGarage';
import { MyGarageServiceCardContainer } from '../MyGarageServiceCardContainer/MyGarageServiceCardContainer';
import { VehicleDetailUpdateModal } from '../modals/VehicleDetailUpdateModal';
import {
  OramSources,
  RegistrationParams,
  ServiceItemParams,
  UpdateTrackedVehicleDetailParams,
} from '../oramInterfaces';
import { useOram } from '../useOram';

type VehiclePanelProps = {
  /** Vehicle report permalink */
  vehiclePermalink: string;
  /** Smartcar */
  Smartcar?: (vin: string) => ReactElement;
};

const ImageContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 272px;
  height: 168px;
`;

const StyledImage = styled('img')`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const StyledText = styled(Text)`
  cursor: pointer;
  text-decoration: underline;
  color: ${({ theme }) => theme.palette.primary.main};
`;

export const MyGarageVehiclePanel: React.FC<VehiclePanelProps> = ({
  vehiclePermalink,
  Smartcar,
}) => {
  const [oramPermalink, setOramPermalink] = useState('');
  const [vehicleReportData, setVehicleReportData] =
    useState<SerializedVehicleReport>();
  const [vehicleDetailUpdateModalOpen, setVehicleDetailUpdateModalOpen] =
    useState(false);
  const [vehicleSearchMethod, setVehicleSearchMethod] = useState({});

  const {
    images: { imgNoVehicleImageProvided },
  } = useAppConstantsContext();
  const queryResult = useReport(vehiclePermalink);
  const { data: rawVehicleReport, isLoading } = queryResult;

  const { redirectToReportUrl } = useReportRedirect();
  const { mutate: handleClaim } = useProcessClaim();
  const { disconnectVehicleMutation } = useDisconnectFromSmartcar(
    vehicleReportData?.meta?.search_data?.smartcar_id
  );

  const {
    data: oramData,
    isLoading: oramDataIsLoading,
    updateTrackedVehicleMutation,
    updateTrackedVehicleDetailMutation,
    createServiceItemMutation,
    completeServiceItemMutation,
    createRegistrationMutation,
  } = useOram(
    oramPermalink,
    vehicleSearchMethod,
    vehicleReportData?.automobiles[0]
  );

  useEffect(() => {
    if (
      rawVehicleReport?.meta?.permalink &&
      rawVehicleReport?.meta?.permalink !== vehicleReportData?.meta?.permalink
    ) {
      setVehicleReportData(SerializeVehicleReport(rawVehicleReport));
    }
  }, [rawVehicleReport?.meta?.permalink]);

  useEffect(() => {
    if (
      vehicleReportData?.meta?.permalink &&
      oramPermalink !== vehiclePermalink
    ) {
      setOramPermalink(vehiclePermalink);

      setVehicleSearchMethod(
        constructClaimedVehicleQueryParam(
          vehicleReportData?.meta?.search_data,
          vehicleReportData?.meta?.search_by as string
        )
      );
    }
  }, [vehicleReportData?.meta?.permalink]);

  if (isLoading) {
    return <LoadingGarage panel="full" />;
  }

  const handleViewReport = (sectionId?: string) => {
    return sectionId
      ? redirectToReportUrl('vehicle', `${vehiclePermalink}#${sectionId}`)
      : redirectToReportUrl('vehicle', vehiclePermalink);
  };

  const handleModalOpen = () => {
    setVehicleDetailUpdateModalOpen(true);
  };

  const handleModalClose = () => {
    setVehicleDetailUpdateModalOpen(false);
  };

  const handleDetailMutation = (
    newFormData: UpdateTrackedVehicleDetailParams
  ) => {
    if (!oramData?.id) return;
    updateTrackedVehicleDetailMutation.mutate({
      currentOramData: {
        mileage: oramData?.mileageReport?.mileage,
        average_mileage: oramData?.averageDrivenMileage?.averageMileage,
        city: oramData?.location?.city,
        state: oramData?.location?.state,
        zip: oramData?.location.zip,
        tracked_vehicle_id: oramData?.id,
        source: oramData?.mileageReport?.source as OramSources,
      },
      newFormData: {
        ...newFormData,
        tracked_vehicle_id: oramData?.id,
        source: 'user',
      },
      vehiclePermalink,
    });
  };

  const handleRerun = async () => {
    const searchMethod = constructClaimedVehicleQueryParam(
      vehicleReportData?.meta?.search_data,
      vehicleReportData?.meta?.search_by as string
    );

    const response = await reportGetters.vehicle({
      queryParams: new URLSearchParams({
        ...searchMethod,
      }),
    });

    redirectToReportUrl('vehicle', response?.report?.permalink);
  };

  const handleRemoveVehicle = async () => {
    if (!vehicleReportData?.meta?.search_data?.smartcar_id) {
      handleRemoveClaimedVehicle();
    } else {
      const response = await disconnectVehicleMutation.mutateAsync();
      if (response.success) {
        handleRemoveClaimedVehicle();
      }
    }
  };

  const handleRemoveClaimedVehicle = () => {
    const formData = {
      report: {
        tag: '',
        permalink: vehiclePermalink,
      },
    };

    updateTrackedVehicleMutation.mutate({
      ...constructClaimedVehicleQueryParam(
        vehicleReportData?.meta?.search_data,
        vehicleReportData?.meta?.search_by as string
      ),
      currently_tracked: false,
    });

    handleClaim(formData);
  };

  const handleCreateServiceItem = ({
    serviceType,
    suggestedChange,
    lastChange,
    source,
  }: ServiceItemParams) => {
    if (!oramData?.id) return;

    const newServiceItem = {
      tracked_vehicle_id: oramData?.id,
      suggested_change: suggestedChange,
      service_type: serviceType,
      last_change: Number(lastChange),
      source,
    };

    createServiceItemMutation.mutate(newServiceItem);

    const showDetailUpdateModal =
      !oramData?.mileageReport?.mileage ||
      !oramData?.averageDrivenMileage?.averageMileage ||
      !oramData?.location?.city;

    if (showDetailUpdateModal) {
      setVehicleDetailUpdateModalOpen(true);
    }
  };

  const handleServiceItemComplete = (
    serviceItemId: number,
    lastChangeMileage: string
  ) => {
    if (!serviceItemId || !oramData?.id) return;

    const obj = {
      tracked_vehicle_id: oramData?.id,
      id: serviceItemId,
      mileage: Number(lastChangeMileage),
    };

    completeServiceItemMutation.mutate(obj);
  };

  const handleCreateRegistration = ({
    expirationDate,
    source,
  }: RegistrationParams) => {
    if (!expirationDate || !oramData?.id) return;

    const obj = {
      tracked_vehicle_id: oramData?.id,
      expiration_date: expirationDate,
      source,
    };

    createRegistrationMutation.mutate(obj);
  };

  const vehicle = vehicleReportData?.automobiles[0];
  const meta = vehicleReportData?.meta;

  const dateUtil = new DateUtil();
  const vehicleSearchBy =
    meta?.search_by === 'vin'
      ? `VIN: ${meta?.search_data?.vin}`
      : `License Plate: ${meta?.search_data?.license_plate}-${meta?.search_data?.state}`;
  const marketValue = commaFormatCurrency(
    vehicle?.valuation?.clean_retail?.adjusted
  );
  const hasMileage =
    oramData?.mileageReport?.mileage || vehicle?.estimated_mileage?.miles;

  const currentMileage = oramData?.mileageReport?.mileage
    ? commaFormat(oramData.mileageReport.mileage)
    : commaFormat(vehicle?.estimated_mileage?.miles);

  const hasYearlyMileage = oramData?.averageDrivenMileage?.averageMileage;
  const yearlyMileage = commaFormat(
    oramData?.averageDrivenMileage?.averageMileage
  );

  const hasLocation =
    Boolean(oramData?.location?.city) && Boolean(oramData?.location?.state);
  const location = `${oramData?.location?.city}, ${oramData?.location?.state}`;

  const lastUpdatedDate = meta?.updated_at
    ? dateUtil.parseDateFromString(
        meta.updated_at,
        'MM/dd/yyyy',
        'yyyy-MM-dd HH:mm:ss ZZZ'
      )
    : 'N/A';

  return (
    <Grid
      container
      spacing={2.5}
      sx={{ mt: 0.5 }}
      data-testid="my-garage-vehicle-panel"
    >
      <Grid item width={{ xs: '100%', md: '292px' }}>
        <Stack spacing={2.5}>
          <ImageContainer>
            {vehicle?.images[0]?.url ? (
              <StyledImage src={vehicle.images[0].url} alt="Vehicle photo" />
            ) : (
              <StyledImage
                src={imgNoVehicleImageProvided}
                alt="Vehicle photo"
              />
            )}
          </ImageContainer>
          <Box>
            <Text variant="h3" sx={{ mb: 0 }}>
              {vehicle?.vehicleYMM}
            </Text>
            <Text variant="caption">{vehicleSearchBy}</Text>
          </Box>
          <Button
            variant="contained"
            onClick={() => handleViewReport()}
            color="success"
          >
            View Report
          </Button>
          <Stack direction="row" alignItems="center">
            <Text variant="h5">Est.value</Text>
            <Tooltip
              title="Market values is an estimate of what the vehicle is valued at based on recent sales of similar vehicles."
              placement="top"
              arrow
            >
              <Icons.InfoOutlined fontSize="small" sx={{ mb: 1.5 }} />
            </Tooltip>
            <Text variant="h5" sx={{ ml: 1 }}>
              {marketValue}
            </Text>
          </Stack>
          <Stack>
            <Stack direction="row" justifyContent="space-between">
              <Stack direction="row" alignItems="center">
                <IconButton
                  disableRipple
                  onClick={handleModalOpen}
                  sx={{ mr: 1.5 }}
                >
                  <Icons.ModeEdit color="primary" />
                </IconButton>
                <Text variant="body2">Current Mileage</Text>
              </Stack>
              {hasMileage ? (
                <Text variant="body2">{currentMileage}</Text>
              ) : (
                <StyledText variant="body2" onClick={handleModalOpen}>
                  Update Now
                </StyledText>
              )}
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Stack direction="row" alignItems="center">
                <IconButton
                  disableRipple
                  onClick={handleModalOpen}
                  sx={{ mr: 1.5 }}
                >
                  <Icons.ModeEdit color="primary" />
                </IconButton>
                <Text variant="body2">Yearly Mileage</Text>
              </Stack>
              {hasYearlyMileage ? (
                <Text variant="body2">{yearlyMileage}</Text>
              ) : (
                <StyledText variant="body2" onClick={handleModalOpen}>
                  Update Now
                </StyledText>
              )}
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Stack direction="row" alignItems="center">
                <IconButton
                  disableRipple
                  onClick={handleModalOpen}
                  sx={{ mr: 1.5 }}
                >
                  <Icons.ModeEdit color="primary" />
                </IconButton>
                <Text variant="body2">Location</Text>
              </Stack>
              {hasLocation ? (
                <Text variant="body2">{location}</Text>
              ) : (
                <StyledText variant="body2" onClick={handleModalOpen}>
                  Update Now
                </StyledText>
              )}
            </Stack>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Text variant="body2">Last updated {lastUpdatedDate}</Text>
            <StyledText variant="body2" onClick={handleRerun}>
              Rerun
            </StyledText>
          </Stack>
          <StyledText
            variant="body2"
            sx={{ mt: 1 }}
            onClick={handleRemoveVehicle}
          >
            Remove Vehicle
          </StyledText>
        </Stack>

        {Smartcar && meta?.search_data?.vin && Smartcar(meta?.search_data?.vin)}
      </Grid>
      <Grid item xs>
        {oramDataIsLoading ? (
          <Grid container spacing={2.5}>
            <LoadingGarage />
          </Grid>
        ) : (
          <MyGarageServiceCardContainer
            vehicle={vehicle}
            oramData={oramData}
            handleViewReport={handleViewReport}
            handleCreateServiceItem={handleCreateServiceItem}
            handleServiceItemComplete={handleServiceItemComplete}
            handleCreateRegistration={handleCreateRegistration}
          />
        )}
      </Grid>
      {vehicle && oramData && (
        <VehicleDetailUpdateModal
          isModalOpen={vehicleDetailUpdateModalOpen}
          closeModal={handleModalClose}
          vehicleYMM={vehicle?.vehicleYMM || ''}
          handleDetailMutation={handleDetailMutation}
          oramData={{
            mileage: oramData.mileageReport.mileage,
            averageMileage: oramData.averageDrivenMileage.averageMileage,
            zip: oramData.location.zip,
            tracked_vehicle_id: oramData.id,
          }}
        />
      )}
    </Grid>
  );
};
